import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

type ResizablePanelContextType = {
  sidePanelWidth: number;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  getDividerProps: () => {
    onMouseDown: (e: React.MouseEvent) => void;
  };
};

const ResizablePanelContext = createContext<ResizablePanelContextType | undefined>(undefined);

export const useResizablePanel = () => {
  const context = useContext(ResizablePanelContext);
  if (!context) throw new Error('useResizablePanel must be used within ResizablePanelProvider');
  return context;
};

export const ResizablePanelProvider = ({
  children,
  initialWidth = 300,
  minWidth = 200,
  maxWidth = 500,
  defaultWidth = 300,
}: {
  children: React.ReactNode;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
}) => {
  const [sidePanelWidth, setSidePanelWidth] = useState(initialWidth);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(initialWidth);
  const previousWidthRef = useRef(initialWidth);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = sidePanelWidth;
    setIsDragging(true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + deltaX;
      setSidePanelWidth(Math.max(minWidth, Math.min(newWidth, maxWidth)));
    },
    [isDragging, maxWidth, minWidth],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const toggleCollapse = useCallback(() => {
    if (isCollapsed) {
      // Expand: restore to previous width or default width
      const widthToRestore = previousWidthRef.current || defaultWidth;
      setSidePanelWidth(widthToRestore);
      setIsCollapsed(false);
    } else {
      // Collapse: save current width and collapse to 0
      previousWidthRef.current = sidePanelWidth;
      setSidePanelWidth(0);
      setIsCollapsed(true);
    }
  }, [isCollapsed, sidePanelWidth, defaultWidth]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const getDividerProps = () => ({
    onMouseDown: handleMouseDown,
  });

  return (
    <ResizablePanelContext.Provider
      value={{ sidePanelWidth, isCollapsed, toggleCollapse, getDividerProps }}
    >
      {children}
    </ResizablePanelContext.Provider>
  );
};
